import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, Truck, FlaskConical, CheckCircle2, XCircle, Package } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import EmptyState from '@/components/ui/EmptyState';
import { useTableExport } from '@/hooks/useTableExport';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustodyLog {
  id: string;
  event: string;
  fromLocation?: string;
  toLocation?: string;
  handedBy?: string;
  receivedBy?: string;
  notes?: string;
  timestamp: string;
}

interface Sample {
  id: string;
  sampleCode: string;
  product: string;
  batchNo?: string;
  matrix: string;
  quantity?: number;
  unit?: string;
  status: string;
  priority: string;
  storageLocation?: string;
  storageTemp?: string;
  collectedAt?: string;
  receivedAt?: string;
  notes?: string;
  lab?: { id: string; name: string };
  createdBy?: { id: string; name: string };
  custodyLog?: CustodyLog[];
  _count?: { aliquots: number; custodyLog: number };
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_SAMPLES: Sample[] = [
  // ── Existing 8 records ──────────────────────────────────────────────────────
  {
    id: '1', sampleCode: 'SMP-000001', product: 'Montelukast Sodium API', batchNo: 'BTH-MTK-2026-0042',
    matrix: 'API', quantity: 500, unit: 'g', status: 'in_testing', priority: 'routine',
    storageLocation: 'Rack A-1', storageTemp: '2-8°C', collectedAt: '2026-03-12T09:00:00Z', receivedAt: '2026-03-12T11:00:00Z',
    lab: { id: 'l1', name: 'QC Central Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel' },
    _count: { aliquots: 2, custodyLog: 3 },
    custodyLog: [
      { id: 'c1', event: 'registered', toLocation: 'Intake Bay', notes: 'Sample registered in LIMS', timestamp: '2026-03-12T09:00:00Z' },
      { id: 'c2', event: 'in_storage', fromLocation: 'Intake Bay', toLocation: 'Rack A-1', handedBy: 'Sneha Patel', timestamp: '2026-03-12T11:00:00Z' },
      { id: 'c3', event: 'assigned_to_testing', fromLocation: 'Rack A-1', toLocation: 'Testing Bench 3', handedBy: 'Sneha Patel', receivedBy: 'Vikram Mehta', timestamp: '2026-03-14T08:30:00Z', notes: 'Assigned for assay and dissolution testing' },
    ],
  },
  {
    id: '2', sampleCode: 'SMP-000002', product: 'Loratadine API', batchNo: 'BTH-LOR-2026-0051',
    matrix: 'API', quantity: 200, unit: 'units', status: 'registered', priority: 'routine',
    storageLocation: 'Rack B-2', storageTemp: '25°C', collectedAt: '2026-03-15T10:00:00Z', receivedAt: '2026-03-15T12:00:00Z',
    lab: { id: 'l1', name: 'QC Central Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel' },
    _count: { aliquots: 0, custodyLog: 1 },
    custodyLog: [
      { id: 'c4', event: 'registered', toLocation: 'Intake Bay', notes: 'Received from production', timestamp: '2026-03-15T10:00:00Z' },
    ],
  },
  {
    id: '3', sampleCode: 'SMP-000003', product: 'Atorvastatin Calcium API', batchNo: 'BTH-ATV-2026-0038',
    matrix: 'API', quantity: 150, unit: 'units', status: 'completed', priority: 'routine',
    storageLocation: 'Archive-C1', storageTemp: '25°C', collectedAt: '2026-02-20T08:00:00Z', receivedAt: '2026-02-20T09:30:00Z',
    lab: { id: 'l2', name: 'API Synthesis Laboratory' }, createdBy: { id: 'u2', name: 'Vikram Mehta' },
    _count: { aliquots: 3, custodyLog: 5 },
    custodyLog: [
      { id: 'c5', event: 'registered', toLocation: 'Intake Bay', timestamp: '2026-02-20T08:00:00Z' },
      { id: 'c6', event: 'in_storage', fromLocation: 'Intake Bay', toLocation: 'Cold Store-1', handedBy: 'Vikram Mehta', timestamp: '2026-02-20T09:30:00Z' },
      { id: 'c7', event: 'assigned_to_testing', fromLocation: 'Cold Store-1', toLocation: 'Testing Bench 1', handedBy: 'Vikram Mehta', receivedBy: 'Ravi Iyer', timestamp: '2026-02-22T08:00:00Z' },
      { id: 'c8', event: 'testing_completed', fromLocation: 'Testing Bench 1', toLocation: 'Archive-C1', handedBy: 'Ravi Iyer', timestamp: '2026-02-25T16:00:00Z', notes: 'All tests passed' },
      { id: 'c9', event: 'archived', toLocation: 'Archive-C1', notes: 'Sample archived post QC release', timestamp: '2026-02-26T09:00:00Z' },
    ],
  },
  {
    id: '4', sampleCode: 'SMP-000004', product: 'Azipen (Azithromycin 500mg Tablets)', batchNo: 'BTH-AZI-2026-0027',
    matrix: 'Tablet', quantity: 100, unit: 'units', status: 'in_testing', priority: 'urgent',
    storageLocation: 'Rack C-3', storageTemp: '25°C', collectedAt: '2026-03-18T07:30:00Z', receivedAt: '2026-03-18T09:00:00Z',
    lab: { id: 'l2', name: 'API Synthesis Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel' },
    _count: { aliquots: 1, custodyLog: 2 },
    custodyLog: [
      { id: 'c10', event: 'registered', toLocation: 'Intake Bay', notes: 'Urgent batch — fast-track', timestamp: '2026-03-18T07:30:00Z' },
      { id: 'c11', event: 'assigned_to_testing', fromLocation: 'Intake Bay', toLocation: 'Testing Bench 2', handedBy: 'Sneha Patel', receivedBy: 'Ravi Iyer', timestamp: '2026-03-18T09:00:00Z' },
    ],
  },
  {
    id: '5', sampleCode: 'SMP-000005', product: 'Amlopen (Amlodipine 5mg Tablets)', batchNo: 'BTH-AML-2026-0019',
    matrix: 'Tablet', quantity: 180, unit: 'units', status: 'completed', priority: 'routine',
    storageLocation: 'Archive-D2', storageTemp: '25°C', collectedAt: '2026-02-10T11:00:00Z', receivedAt: '2026-02-10T13:00:00Z',
    lab: { id: 'l4', name: 'Contract Testing Laboratory' }, createdBy: { id: 'u2', name: 'Vikram Mehta' },
    _count: { aliquots: 2, custodyLog: 4 },
    custodyLog: [
      { id: 'c12', event: 'registered', toLocation: 'Intake Bay', timestamp: '2026-02-10T11:00:00Z' },
      { id: 'c13', event: 'in_storage', fromLocation: 'Intake Bay', toLocation: 'Rack D-1', handedBy: 'Vikram Mehta', timestamp: '2026-02-10T13:00:00Z' },
      { id: 'c14', event: 'assigned_to_testing', fromLocation: 'Rack D-1', toLocation: 'HPLC Lab', handedBy: 'Vikram Mehta', receivedBy: 'Pooja Sharma', timestamp: '2026-02-12T08:30:00Z' },
      { id: 'c15', event: 'testing_completed', fromLocation: 'HPLC Lab', toLocation: 'Archive-D2', handedBy: 'Pooja Sharma', timestamp: '2026-02-14T17:00:00Z', notes: 'HPLC assay complete, results uploaded' },
    ],
  },
  {
    id: '6', sampleCode: 'SMP-000006', product: 'Rosuvastatin Calcium API', batchNo: 'BTH-RSV-2026-007',
    matrix: 'Powder', quantity: 500, unit: 'g', status: 'in_storage', priority: 'routine',
    storageLocation: 'Cold Store-2', storageTemp: '2-8°C', collectedAt: '2026-03-01T09:00:00Z', receivedAt: '2026-03-01T10:30:00Z',
    lab: { id: 'l1', name: 'QC Central Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel' },
    _count: { aliquots: 5, custodyLog: 2 },
    custodyLog: [
      { id: 'c16', event: 'registered', toLocation: 'Intake Bay', notes: 'API batch for QC testing', timestamp: '2026-03-01T09:00:00Z' },
      { id: 'c17', event: 'in_storage', fromLocation: 'Intake Bay', toLocation: 'Cold Store-2', handedBy: 'Sneha Patel', timestamp: '2026-03-01T10:30:00Z', notes: 'Stored at 2-8°C per SOP-STR-004' },
    ],
  },
  {
    id: '7', sampleCode: 'SMP-000007', product: 'Montelukast Sodium Stability Sample (T=0)', batchNo: 'BTH-MTK-STB-2025-001',
    matrix: 'Tablet', quantity: 300, unit: 'units', status: 'in_storage', priority: 'routine',
    storageLocation: 'Stability Chamber 1', storageTemp: '25°C/60%RH',
    collectedAt: '2025-01-01T00:00:00Z', receivedAt: '2025-01-01T00:00:00Z',
    lab: { id: 'l3', name: 'Stability Testing Laboratory' }, createdBy: { id: 'u2', name: 'Vikram Mehta' },
    _count: { aliquots: 6, custodyLog: 3 },
    custodyLog: [
      { id: 'c18', event: 'registered', toLocation: 'Intake Bay', notes: 'Stability study T=0 sample', timestamp: '2025-01-01T00:00:00Z' },
      { id: 'c19', event: 'in_storage', fromLocation: 'Intake Bay', toLocation: 'Stability Chamber 1', handedBy: 'Vikram Mehta', timestamp: '2025-01-01T06:00:00Z', notes: 'Placed in 25°C/60%RH chamber' },
      { id: 'c20', event: 'aliquot_withdrawn', fromLocation: 'Stability Chamber 1', toLocation: 'Testing Bench 4', handedBy: 'Ravi Iyer', timestamp: '2025-07-01T09:00:00Z', notes: 'T=6M stability pull' },
    ],
  },
  {
    id: '8', sampleCode: 'SMP-000008', product: 'Apixaban API', batchNo: 'BTH-APX-2026-0055',
    matrix: 'Tablet', quantity: 220, unit: 'units', status: 'in_testing', priority: 'routine',
    storageLocation: 'Rack A-4', storageTemp: '25°C', collectedAt: '2026-03-20T07:00:00Z', receivedAt: '2026-03-20T08:30:00Z',
    lab: { id: 'l1', name: 'QC Central Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel' },
    _count: { aliquots: 1, custodyLog: 2 },
  },

      // ── 2023 batch ──────────────────────────────────────────────────────────────
  { id: '9',  sampleCode: 'SMP-2023-009', product: 'Empagliflozin API',                        batchNo: 'BTH-EMP-23001',       matrix: 'API',       quantity: 600,  unit: 'g',      status: 'completed',  priority: 'routine', storageLocation: 'Archive-API-1',       storageTemp: '2–8°C',      collectedAt: '2023-04-05T08:00:00Z', receivedAt: '2023-04-05T09:30:00Z', lab: { id: 'l1', name: 'QC Central Laboratory'            }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 2, custodyLog: 4 } },
  { id: '10', sampleCode: 'SMP-2023-010', product: 'Ceftriaxone Sodium API',                   batchNo: 'BTH-CFT-23001',       matrix: 'API',       quantity: 400,  unit: 'g',      status: 'completed',  priority: 'routine', storageLocation: 'Archive-API-2',       storageTemp: '2–8°C',      collectedAt: '2023-05-10T10:00:00Z', receivedAt: '2023-05-10T11:30:00Z', lab: { id: 'l2', name: 'API Synthesis Laboratory'         }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 1, custodyLog: 3 } },
  { id: '11', sampleCode: 'SMP-2023-011', product: 'Azithromycin API',                         batchNo: 'BTH-AZT-23001',       matrix: 'API',       quantity: 300,  unit: 'g',      status: 'disposed',   priority: 'routine', storageLocation: 'Disposed',            storageTemp: '2–8°C',      collectedAt: '2023-06-15T09:00:00Z', receivedAt: '2023-06-15T10:00:00Z', lab: { id: 'l1', name: 'QC Central Laboratory'            }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 0, custodyLog: 5 } },
  { id: '12', sampleCode: 'SMP-2023-012', product: 'Amikacin Sulfate 500mg/2mL Injection',     batchNo: 'BTH-AMK-23001',       matrix: 'Injection', quantity: 20,   unit: 'vials',  status: 'completed',  priority: 'urgent',  storageLocation: 'Archive-Sterile-1',   storageTemp: '15–25°C',    collectedAt: '2023-07-20T08:00:00Z', receivedAt: '2023-07-20T09:00:00Z', lab: { id: 'l5', name: 'Sterile Injectable QC Laboratory' }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 3, custodyLog: 4 } },
  { id: '13', sampleCode: 'SMP-2023-013', product: 'Burnol Antiseptic Cream 30g',              batchNo: 'BTH-BRN-23005',       matrix: 'Cream',     quantity: 60,   unit: 'tubes',  status: 'completed',  priority: 'routine', storageLocation: 'Archive-Consumer-1',  storageTemp: '25°C',       collectedAt: '2023-08-12T07:30:00Z', receivedAt: '2023-08-12T09:00:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 2, custodyLog: 3 } },
  { id: '14', sampleCode: 'SMP-2023-014', product: 'Loratadine API (Export)',                  batchNo: 'BTH-LOR-23004',       matrix: 'API',       quantity: 1000, unit: 'g',      status: 'completed',  priority: 'stat',    storageLocation: 'Archive-API-3',       storageTemp: '2–8°C',      collectedAt: '2023-09-01T06:00:00Z', receivedAt: '2023-09-01T07:30:00Z', lab: { id: 'l7', name: 'Contract Testing Laboratory'      }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 6, custodyLog: 5 } },
  { id: '15', sampleCode: 'SMP-2023-015', product: 'Atorvastatin Calcium API Stability T=0',  batchNo: 'BTH-ATV-STB-2023',    matrix: 'API',       quantity: 360,  unit: 'g',      status: 'in_storage', priority: 'routine', storageLocation: 'Stability Chamber 2', storageTemp: '25°C/60%RH', collectedAt: '2023-10-01T00:00:00Z', receivedAt: '2023-10-01T00:00:00Z', lab: { id: 'l3', name: 'Stability Testing Laboratory'      }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 6, custodyLog: 4 } },

  // ── 2024 batch ──────────────────────────────────────────────────────────────
  { id: '16', sampleCode: 'SMP-2024-016', product: 'Gabapentin 400mg Capsules',                batchNo: 'BTH-GBP-24001',       matrix: 'Capsule',   quantity: 180,  unit: 'units',  status: 'completed',  priority: 'routine', storageLocation: 'Archive-FD-1',        storageTemp: '25°C',       collectedAt: '2024-01-10T08:00:00Z', receivedAt: '2024-01-10T09:00:00Z', lab: { id: 'l6', name: 'Finished Dose QC Laboratory'      }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 2, custodyLog: 4 } },
  { id: '17', sampleCode: 'SMP-2024-017', product: 'Ceftriaxone 1g Powder for Injection',     batchNo: 'BTH-CFJ-24001',       matrix: 'Injection', quantity: 15,   unit: 'vials',  status: 'in_testing', priority: 'urgent',  storageLocation: 'Testing Bench 3',     storageTemp: '15–25°C',    collectedAt: '2024-02-14T09:00:00Z', receivedAt: '2024-02-14T10:30:00Z', lab: { id: 'l5', name: 'Sterile Injectable QC Laboratory' }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 2, custodyLog: 3 } },
  { id: '18', sampleCode: 'SMP-2024-018', product: 'GlucoOne BG-03 Glucometer',                batchNo: 'BTH-BGM-24003',       matrix: 'Device',    quantity: 20,   unit: 'units',  status: 'completed',  priority: 'stat',    storageLocation: 'Archive-Device-1',    storageTemp: '25°C',       collectedAt: '2024-03-05T07:00:00Z', receivedAt: '2024-03-05T08:00:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 4, custodyLog: 5 } },
  { id: '19', sampleCode: 'SMP-2024-019', product: 'Rosuvastatin Calcium API',                 batchNo: 'BTH-RSV-24001',       matrix: 'API',       quantity: 500,  unit: 'g',      status: 'in_transit', priority: 'routine', storageLocation: 'In Transit',          storageTemp: '2–8°C',      collectedAt: '2024-04-10T09:00:00Z', receivedAt: '2024-04-10T10:00:00Z', lab: { id: 'l2', name: 'API Synthesis Laboratory'         }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 0, custodyLog: 2 } },
  { id: '20', sampleCode: 'SMP-2024-020', product: 'Metronidazole 500mg/100mL Infusion',      batchNo: 'BTH-MTZ-23001',       matrix: 'Injection', quantity: 12,   unit: 'bags',   status: 'completed',  priority: 'urgent',  storageLocation: 'Archive-Sterile-2',   storageTemp: '15–25°C',    collectedAt: '2024-05-18T06:30:00Z', receivedAt: '2024-05-18T08:00:00Z', lab: { id: 'l5', name: 'Sterile Injectable QC Laboratory' }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 3, custodyLog: 4 } },
  { id: '21', sampleCode: 'SMP-2024-021', product: 'Apixaban API Stability T=0',               batchNo: 'BTH-APX-STB-2024',    matrix: 'API',       quantity: 500,  unit: 'g',      status: 'in_storage', priority: 'routine', storageLocation: 'Stability Chamber 3', storageTemp: '25°C/60%RH', collectedAt: '2024-06-01T10:00:00Z', receivedAt: '2024-06-01T11:30:00Z', lab: { id: 'l3', name: 'Stability Testing Laboratory'      }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 5, custodyLog: 2 } },
  { id: '22', sampleCode: 'SMP-2024-022', product: 'Ayurvedic Chyawanprash 450g',              batchNo: 'BTH-CHY-24001',       matrix: 'Cream',     quantity: 24,   unit: 'jars',   status: 'rejected',   priority: 'routine', storageLocation: 'Rejected Hold',       storageTemp: '25°C',       collectedAt: '2024-07-22T08:00:00Z', receivedAt: '2024-07-22T09:00:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 0, custodyLog: 3 } },
  { id: '23', sampleCode: 'SMP-2024-023', product: 'Meropenem Trihydrate API',                 batchNo: 'BTH-MEM-24001',       matrix: 'API',       quantity: 600,  unit: 'g',      status: 'completed',  priority: 'routine', storageLocation: 'Archive-API-5',       storageTemp: '2–8°C',      collectedAt: '2024-08-05T07:00:00Z', receivedAt: '2024-08-05T08:30:00Z', lab: { id: 'l7', name: 'Contract Testing Laboratory'      }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 4, custodyLog: 4 } },
  { id: '24', sampleCode: 'SMP-2024-024', product: 'Surface Swab — API Blending Area',        batchNo: 'ENV-QC-2024-024',     matrix: 'Swab',      quantity: 5,    unit: 'swabs',  status: 'completed',  priority: 'routine', storageLocation: 'Archive-ENV-1',       storageTemp: '2–8°C',      collectedAt: '2024-09-10T06:00:00Z', receivedAt: '2024-09-10T07:00:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 0, custodyLog: 3 } },
  { id: '25', sampleCode: 'SMP-2024-025', product: 'Empagliflozin API Stability Zone II T=0', batchNo: 'BTH-EMP-STB-2024',    matrix: 'API',       quantity: 300,  unit: 'g',      status: 'in_storage', priority: 'routine', storageLocation: 'Stability Chamber 4', storageTemp: '25°C/60%RH', collectedAt: '2024-10-01T00:00:00Z', receivedAt: '2024-10-01T00:00:00Z', lab: { id: 'l3', name: 'Stability Testing Laboratory'      }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 6, custodyLog: 3 } },
  { id: '26', sampleCode: 'SMP-2024-026', product: 'Losartan Potassium 50mg Tablets',         batchNo: 'BTH-LST-24001',       matrix: 'Tablet',    quantity: 350,  unit: 'units',  status: 'in_testing', priority: 'routine', storageLocation: 'Rack D-2',            storageTemp: '25°C',       collectedAt: '2024-11-14T09:00:00Z', receivedAt: '2024-11-14T10:00:00Z', lab: { id: 'l6', name: 'Finished Dose QC Laboratory'      }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 1, custodyLog: 2 } },
  { id: '27', sampleCode: 'SMP-2024-027', product: 'Equipment Swab — Granulator 2 (API Plant)',batchNo: 'ENV-QC-2024-027',    matrix: 'Swab',      quantity: 3,    unit: 'swabs',  status: 'completed',  priority: 'stat',    storageLocation: 'Archive-ENV-2',       storageTemp: '2–8°C',      collectedAt: '2024-12-02T06:00:00Z', receivedAt: '2024-12-02T07:00:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 0, custodyLog: 3 } },

  // ── 2025 batch ──────────────────────────────────────────────────────────────
  { id: '28', sampleCode: 'SMP-2025-028', product: 'Montelukast Sodium API',                  batchNo: 'BTH-MTK-25001',       matrix: 'API',       quantity: 200,  unit: 'g',      status: 'completed',  priority: 'routine', storageLocation: 'Archive-API-6',       storageTemp: '2–8°C',      collectedAt: '2025-01-15T08:00:00Z', receivedAt: '2025-01-15T09:30:00Z', lab: { id: 'l1', name: 'QC Central Laboratory'            }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 2, custodyLog: 4 } },
  { id: '29', sampleCode: 'SMP-2025-029', product: 'Pregabalin 75mg Capsules',                batchNo: 'BTH-PRG-25001',       matrix: 'Capsule',   quantity: 200,  unit: 'units',  status: 'in_storage', priority: 'urgent',  storageLocation: 'Cold Store-4',        storageTemp: '25°C',       collectedAt: '2025-02-20T09:00:00Z', receivedAt: '2025-02-20T10:30:00Z', lab: { id: 'l6', name: 'Finished Dose QC Laboratory'      }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 3, custodyLog: 2 } },
  { id: '30', sampleCode: 'SMP-2025-030', product: 'Ceftriaxone 1g Stability T=0',            batchNo: 'BTH-CFJ-STB-2025',    matrix: 'Injection', quantity: 30,   unit: 'vials',  status: 'in_transit', priority: 'urgent',  storageLocation: 'In Transit',          storageTemp: '15–25°C',    collectedAt: '2025-03-08T07:00:00Z', receivedAt: '2025-03-08T08:30:00Z', lab: { id: 'l3', name: 'Stability Testing Laboratory'      }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 0, custodyLog: 2 } },
  { id: '31', sampleCode: 'SMP-2025-031', product: 'Linezolid 600mg Tablets',                 batchNo: 'BTH-LNZ-24001',       matrix: 'Tablet',    quantity: 210,  unit: 'units',  status: 'in_testing', priority: 'routine', storageLocation: 'Rack E-1',            storageTemp: '25°C',       collectedAt: '2025-04-12T09:00:00Z', receivedAt: '2025-04-12T10:00:00Z', lab: { id: 'l7', name: 'Contract Testing Laboratory'      }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 2, custodyLog: 2 } },
  { id: '32', sampleCode: 'SMP-2025-032', product: 'Rosuvastatin Calcium API (large batch)',  batchNo: 'BTH-RSV-25002',       matrix: 'API',       quantity: 600,  unit: 'g',      status: 'in_storage', priority: 'routine', storageLocation: 'Cold Store-5',        storageTemp: '2–8°C',      collectedAt: '2025-05-20T08:00:00Z', receivedAt: '2025-05-20T09:30:00Z', lab: { id: 'l7', name: 'Contract Testing Laboratory'      }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 4, custodyLog: 2 } },
  { id: '33', sampleCode: 'SMP-2025-033', product: 'Azithromycin API',                        batchNo: 'BTH-AZT-25001',       matrix: 'API',       quantity: 400,  unit: 'g',      status: 'completed',  priority: 'routine', storageLocation: 'Archive-API-7',       storageTemp: '2–8°C',      collectedAt: '2025-06-05T07:30:00Z', receivedAt: '2025-06-05T09:00:00Z', lab: { id: 'l1', name: 'QC Central Laboratory'            }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 2, custodyLog: 4 } },
  { id: '34', sampleCode: 'SMP-2025-034', product: 'Air Monitoring — Sterile Fill Zone A',    batchNo: 'ENV-QC-2025-034',     matrix: 'Swab',      quantity: 4,    unit: 'plates', status: 'completed',  priority: 'routine', storageLocation: 'Archive-ENV-3',       storageTemp: '2–8°C',      collectedAt: '2025-07-14T05:30:00Z', receivedAt: '2025-07-14T06:30:00Z', lab: { id: 'l5', name: 'Sterile Injectable QC Laboratory' }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 0, custodyLog: 2 } },
  { id: '35', sampleCode: 'SMP-2025-035', product: 'Edoxaban API',                            batchNo: 'BTH-EDX-25001',       matrix: 'API',       quantity: 500,  unit: 'g',      status: 'in_transit', priority: 'routine', storageLocation: 'In Transit',          storageTemp: '2–8°C',      collectedAt: '2025-08-22T09:00:00Z', receivedAt: '2025-08-22T10:00:00Z', lab: { id: 'l2', name: 'API Synthesis Laboratory'         }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 1, custodyLog: 2 } },
  { id: '36', sampleCode: 'SMP-2025-036', product: 'GlucoOne BG-03 ISO 15197 Accuracy',      batchNo: 'BTH-BGM-25001',       matrix: 'Device',    quantity: 15,   unit: 'units',  status: 'in_storage', priority: 'urgent',  storageLocation: 'Rack F-2',            storageTemp: '25°C',       collectedAt: '2025-09-30T08:00:00Z', receivedAt: '2025-09-30T09:30:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 2, custodyLog: 2 } },
  { id: '37', sampleCode: 'SMP-2025-037', product: 'Azipen (Azithromycin 500mg Tablets)',     batchNo: 'BTH-AZI-25003',       matrix: 'Tablet',    quantity: 145,  unit: 'units',  status: 'in_testing', priority: 'routine', storageLocation: 'Rack G-1',            storageTemp: '25°C',       collectedAt: '2025-10-18T09:00:00Z', receivedAt: '2025-10-18T10:00:00Z', lab: { id: 'l6', name: 'Finished Dose QC Laboratory'      }, createdBy: { id: 'u3', name: 'Ravi Iyer'       }, _count: { aliquots: 1, custodyLog: 2 } },
  { id: '38', sampleCode: 'SMP-2025-038', product: 'Apixaban API',                            batchNo: 'BTH-APX-25001',       matrix: 'API',       quantity: 350,  unit: 'g',      status: 'disposed',   priority: 'routine', storageLocation: 'Disposed',            storageTemp: '2–8°C',      collectedAt: '2025-11-10T08:00:00Z', receivedAt: '2025-11-10T09:00:00Z', lab: { id: 'l1', name: 'QC Central Laboratory'            }, createdBy: { id: 'u2', name: 'Vikram Mehta'    }, _count: { aliquots: 0, custodyLog: 4 } },

  // ── 2026 additional batch ────────────────────────────────────────────────────
  { id: '39', sampleCode: 'SMP-2026-039', product: 'Burnol Antiseptic Cream 30g Stability',   batchNo: 'BTH-BRN-STB-2026',    matrix: 'Cream',     quantity: 36,   unit: 'tubes',  status: 'registered', priority: 'routine', storageLocation: 'Intake Bay',          storageTemp: '25°C',       collectedAt: '2026-03-28T09:00:00Z', receivedAt: '2026-03-28T10:30:00Z', lab: { id: 'l4', name: 'Microbiology Laboratory'           }, createdBy: { id: 'u1', name: 'Sneha Patel'     }, _count: { aliquots: 0, custodyLog: 1 } },
  { id: '40', sampleCode: 'SMP-2026-040', product: 'Metronidazole 500mg/100mL Infusion OOS', batchNo: 'BTH-MTZ-24001',        matrix: 'Injection', quantity: 10,   unit: 'bags',   status: 'in_storage', priority: 'stat',    storageLocation: 'Quarantine-Hold-1',   storageTemp: '15–25°C',    collectedAt: '2026-04-02T07:30:00Z', receivedAt: '2026-04-02T09:00:00Z', lab: { id: 'l5', name: 'Sterile Injectable QC Laboratory' }, createdBy: { id: 'u4', name: 'Pooja Sharma'    }, _count: { aliquots: 2, custodyLog: 2 } },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['registered', 'in_storage', 'in_transit', 'in_testing', 'completed', 'disposed', 'rejected'];
const PRIORITY_OPTIONS = ['routine', 'urgent', 'stat'];
const MATRIX_OPTIONS = ['Tablet', 'Capsule', 'Liquid', 'Injection', 'Cream', 'Powder', 'Suspension', 'API', 'Swab', 'Water', 'Other'];

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Samples() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [form, setForm] = useState({
    labId: '', product: '', batchNo: '', matrix: '', quantity: '', unit: 'units',
    priority: 'routine', storageLocation: '', storageTemp: '', notes: '',
  });

  const queryClient = useQueryClient();

  const { data: labsData } = useQuery({
    queryKey: ['labs-list'],
    queryFn: () => api.get('/labs', { params: { limit: 50 } }).then((r) => r.data).catch(() => ({ data: [] })),
  });

  const allLabs: { id: string; name: string }[] = Array.isArray(labsData?.data) ? labsData.data : [];

  // ── Client-side filtering on MOCK_SAMPLES (avoids interceptor instability) ──
  const filteredSamples = useMemo(() => {
    let items = MOCK_SAMPLES;
    if (statusFilter) items = items.filter((s) => s.status === statusFilter);
    if (priorityFilter) items = items.filter((s) => s.priority === priorityFilter);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (s) =>
          s.sampleCode.toLowerCase().includes(q) ||
          s.product.toLowerCase().includes(q) ||
          (s.batchNo?.toLowerCase().includes(q) ?? false)
      );
    }
    return items;
  }, [search, statusFilter, priorityFilter]);

  const PAGE_SIZE = 20;
  const rows: Sample[] = filteredSamples.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total: number = filteredSamples.length;
  const isLoading = false;
  const sampleDetail: Sample | null = selectedSample ?? null;

  // Stats always reflect the FULL dataset (no filter applied to cards)
  const stats = {
    total:     MOCK_SAMPLES.length,
    inTransit: MOCK_SAMPLES.filter((s) => s.status === 'in_transit').length,
    inTesting: MOCK_SAMPLES.filter((s) => s.status === 'in_testing').length,
    completed: MOCK_SAMPLES.filter((s) => s.status === 'completed').length,
    rejected:  MOCK_SAMPLES.filter((s) => s.status === 'rejected').length,
  };

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) =>
      api.post('/lims-samples', {
        ...payload,
        quantity: payload.quantity ? Number(payload.quantity) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-samples'] });
      setShowCreate(false);
      setForm({ labId: '', product: '', batchNo: '', matrix: '', quantity: '', unit: 'units', priority: 'routine', storageLocation: '', storageTemp: '', notes: '' });
    },
  });

  const exportColumns = [
    { key: 'sampleCode', label: 'Sample Code' },
    { key: 'product', label: 'Product' },
    { key: 'matrix', label: 'Matrix' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
  ];
  const { exportCSV, exportPDF } = useTableExport(rows as unknown as Record<string, unknown>[], exportColumns, 'samples-list');

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'sampleCode', label: 'Sample Code', width: 'w-36',
      render: (row) => (
        <span className="font-mono text-xs font-semibold text-teal-700">{String(row.sampleCode)}</span>
      ),
    },
    {
      key: 'product', label: 'Product / Matrix',
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-text">{String(row.product)}</p>
          <p className="text-xs text-text-dim">{String(row.matrix)}{row.batchNo ? ` · ${String(row.batchNo)}` : ''}</p>
        </div>
      ),
    },
    {
      key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string } | undefined;
        return <span className="text-xs text-text-muted">{lab?.name ?? '—'}</span>;
      },
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'priority', label: 'Priority', width: 'w-24',
      render: (row) => {
        const p = String(row.priority);
        const colorMap: Record<string, string> = { stat: 'bg-red-100 text-red-700', urgent: 'bg-orange-100 text-orange-700', routine: 'bg-gray-100 text-gray-600' };
        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${colorMap[p] || 'bg-gray-100 text-gray-600'}`}>{p}</span>;
      },
    },
    {
      key: 'collectedAt', label: 'Collected', width: 'w-28',
      render: (row) => (
        <span className="text-xs font-mono text-text-muted">
          {row.collectedAt ? format(new Date(String(row.collectedAt)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
    {
      key: '_count', label: 'Custody Events', width: 'w-28',
      render: (row) => {
        const cnt = row._count as { aliquots: number; custodyLog: number } | undefined;
        return <span className="text-xs text-text-muted">{cnt?.custodyLog ?? 0} events</span>;
      },
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Sample & Specimen Tracking"
        subtitle="Chain of custody · GMP compliant"
        actions={
          <>
            <button onClick={exportPDF} className="btn-ghost"><FileText size={13} /> PDF</button>
            <button onClick={exportCSV} className="btn-ghost"><Download size={13} /> CSV</button>
            <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> Log Sample</button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Samples" value={stats.total} icon={FlaskConical} color="#b07d1a" />
        <StatCard label="In Transit" value={stats.inTransit} icon={Truck} color="#D97706" />
        <StatCard label="In Testing" value={stats.inTesting} icon={FlaskConical} color="#2563EB" />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="Rejected" value={stats.rejected} icon={XCircle} color="#DC2626" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by sample code, product, batch..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {['', ...STATUS_OPTIONS].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${statusFilter === s ? 'bg-teal text-white' : 'bg-bg text-text-muted border border-border hover:border-teal/40'}`}
            >
              {s ? s.replace(/_/g, ' ') : 'All Status'}
            </button>
          ))}
        </div>
        <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }} className="select w-32">
          <option value="">All Priority</option>
          {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedSample(row as unknown as Sample)}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Sample Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedSample}
        onClose={() => setSelectedSample(null)}
        title={selectedSample?.sampleCode || ''}
        subtitle={`${selectedSample?.product} · ${selectedSample?.matrix}`}
        width={560}
      >
        {selectedSample && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Status', value: <PillBadge value={selectedSample.status} showDot /> },
                { label: 'Priority', value: selectedSample.priority },
                { label: 'Lab', value: selectedSample.lab?.name || '—' },
                { label: 'Matrix', value: selectedSample.matrix },
                { label: 'Batch No.', value: selectedSample.batchNo || '—' },
                { label: 'Quantity', value: selectedSample.quantity ? `${selectedSample.quantity} ${selectedSample.unit || ''}` : '—' },
                { label: 'Storage', value: selectedSample.storageLocation || '—' },
                { label: 'Temperature', value: selectedSample.storageTemp || '—' },
                { label: 'Collected', value: selectedSample.collectedAt ? format(new Date(selectedSample.collectedAt), 'd MMM yyyy') : '—' },
                { label: 'Received', value: selectedSample.receivedAt ? format(new Date(selectedSample.receivedAt), 'd MMM yyyy') : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 rounded-lg bg-bg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <div className="text-xs font-medium text-text">{typeof value === 'string' ? value : value}</div>
                </div>
              ))}
            </div>

            {selectedSample.notes && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Notes</p>
                <p className="text-sm text-text leading-relaxed">{selectedSample.notes}</p>
              </div>
            )}

            {/* Chain of Custody Timeline */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-3 flex items-center gap-1.5">
                <Truck size={12} className="text-teal" /> Chain of Custody
              </p>
              {!sampleDetail?.custodyLog?.length ? (
                <EmptyState icon="data" title="No custody events" description="Custody events will appear here as the sample moves." />
              ) : (
                <div className="space-y-0">
                  {(sampleDetail.custodyLog || []).map((event, idx) => (
                    <div key={event.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ background: '#b07d1a' }}>
                          {idx + 1}
                        </div>
                        {idx < (sampleDetail.custodyLog?.length || 0) - 1 && (
                          <div className="w-px flex-1 my-1 bg-border" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="text-xs font-semibold text-text capitalize">{event.event.replace(/_/g, ' ')}</p>
                        {(event.fromLocation || event.toLocation) && (
                          <p className="text-xs text-text-muted mt-0.5">
                            {event.fromLocation && <span>{event.fromLocation} </span>}
                            {event.fromLocation && event.toLocation && <span className="text-teal">→ </span>}
                            {event.toLocation}
                          </p>
                        )}
                        <p className="text-[11px] text-text-dim mt-0.5">
                          {format(new Date(event.timestamp), 'd MMM yyyy HH:mm')}
                          {event.handedBy && ` · by ${event.handedBy}`}
                          {event.receivedBy && ` · received by ${event.receivedBy}`}
                        </p>
                        {event.notes && <p className="text-[11px] text-text-muted mt-1 italic">{event.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Log Sample Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Log New Sample"
        subtitle="Chain of custody begins at registration"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.labId || !form.product || !form.matrix}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Logging...' : 'Log Sample'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Product *</label>
            <input
              className="input"
              value={form.product}
              onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
              placeholder="e.g. Amoxicillin 500mg Capsules"
            />
          </div>
          <div>
            <label className="label">Matrix *</label>
            <select className="select" value={form.matrix} onChange={(e) => setForm((f) => ({ ...f, matrix: e.target.value }))}>
              <option value="">Select matrix...</option>
              {MATRIX_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Lab *</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {allLabs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Batch No.</label>
            <input
              className="input"
              value={form.batchNo}
              onChange={(e) => setForm((f) => ({ ...f, batchNo: e.target.value }))}
              placeholder="e.g. BATCH-2026-0042"
            />
          </div>
          <div>
            <label className="label">Priority</label>
            <select className="select" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Quantity</label>
            <div className="flex gap-2">
              <input
                type="number"
                className="input flex-1"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                placeholder="0"
              />
              <select className="select w-28" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}>
                {['units', 'g', 'mg', 'mL', 'L', 'vials'].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Storage Location</label>
            <input
              className="input"
              value={form.storageLocation}
              onChange={(e) => setForm((f) => ({ ...f, storageLocation: e.target.value }))}
              placeholder="e.g. Rack A-1, Cold Store 2"
            />
          </div>
          <div>
            <label className="label">Storage Temp</label>
            <select className="select" value={form.storageTemp} onChange={(e) => setForm((f) => ({ ...f, storageTemp: e.target.value }))}>
              <option value="">Select...</option>
              {['2-8°C', '15-25°C', '25°C', '-20°C', '-80°C', 'Ambient'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Collection conditions, special handling..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
